import type { RuleObject } from 'ant-design-vue/lib/form';
import type { RuleType } from 'ant-design-vue/lib/form/interface';
import dayjs, { type Dayjs } from 'dayjs';

///
/// https://github.com/yiminghe/async-validator
///

export const required = (message = 'This input is required'): RuleObject => ({
  message,
  required: true,
  whitespace: true,
});

export const requiredNumber = (min?: number, message = 'This value is required'): RuleObject => ({
  required: true,
  type: 'number',
  min,
  message,
});

export const email = (message = 'It is not a valid email'): RuleObject => ({
  type: 'email',
  message,
  validateTrigger: 'change',
});

export const emailRequired = (message: string = 'Please input email'): RuleObject[] => [required(message), email()];

export const validateConfirmPass = async (value: string, password?: string) => {
  if (value && value !== password) {
    return Promise.reject(new Error("Two inputs don't match!"));
  }
  return Promise.resolve();
};

export const lowercase = (message = 'Your password must contain at least one lowecase letter.'): RuleObject => ({
  pattern: /(?=.*[a-z])/,
  validateTrigger: 'change',
  message,
});

export const uppercase = (message = 'Your password must contain at least one upper letter.'): RuleObject => ({
  pattern: /(?=.*[A-Z])/,
  validateTrigger: 'change',
  message,
});

export const digit = (message = 'Your password must contain at least one digit.'): RuleObject => ({
  pattern: /[0-9]/i,
  validateTrigger: 'change',
  message,
});

export const specialChar = (message = 'Your password must contain at least special char from -[ ! @ # $ % ^ & * _ ]'): RuleObject => ({
  pattern: /(?=.*[!@#$%^&*_])/,
  validateTrigger: 'change',
  message,
});

export const requiredArray = (message = 'Please select a date range.'): RuleObject => ({
  required: true,
  message,
  validateTrigger: 'change',
  type: 'array',
});

export const requiredObject = (message = 'This input is required.'): RuleObject => ({
  required: true,
  message,
  validateTrigger: 'change',
  type: 'object',
});

// only allow alphanumeric characters and hypens and underscores
export const alphanumericRequired = (message = 'Only alphanumeric characters, hyphens and underscores are allowed.'): RuleObject => ({
  required: true,
  pattern: /^[a-zA-Z0-9_-]*$/,
  message,
  validateTrigger: 'change',
});

export const trueOnlyRequired = (message = 'Please check it'): RuleObject => ({
  required: true,
  type: 'boolean',
  validator: (_, value) => {
    if (value !== true) {
      return Promise.reject(new Error(message));
    }
    return Promise.resolve();
  },
});

export const booleanRequired = (message = 'Please check it'): RuleObject => ({
  required: true,
  type: 'boolean',
  message,
});

export const phoneUS = (message = 'Please enter a valid US phone number'): RuleObject => ({
  pattern: /^(?=(?:\D*\d){10}\D*$)[+\d\s().-]+$/,
  message,
  validateTrigger: 'change',
});

export const phoneNonUS = (message = 'Please enter a valid non-US phone number'): RuleObject => ({
  pattern: /^(?=(?:\D*\d){7,15}\D*$)[+\d\s().-]+$/,
  message,
  validateTrigger: 'change',
});

export const radioRequired = (message = 'Please select an option', type: RuleType = 'number'): RuleObject => ({
  required: true,
  type,
  message,
  validateTrigger: 'change',
});

export const dateAfter = (date: Dayjs, message: string | undefined = undefined): RuleObject => ({
  validator: (_, value: Dayjs | undefined) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value.isAfter(date) || value.isSame(date, 'day')) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(
      message ?? `Date must be after ${date.format('MM/DD/YYYY')}`,
    ));
  },
});

export const dateEquals = (date: Dayjs, message: string | undefined = undefined): RuleObject => ({
  validator: (_, value: Dayjs | undefined) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value.isSame(date, 'day')) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(message ?? `Date must be equal to ${date.format('MM/DD/YYYY')}`));
  },
});

export const dateAfterOrEqual = (date: Dayjs, message: string | undefined = undefined): RuleObject => ({
  validator: (_, value: Dayjs | undefined) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value.isAfter(date) || value.isSame(date, 'day')) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(
      message ?? `Date must be after or equal to ${date.format('MM/DD/YYYY')}`,
    ));
  },
});

export const dateBefore = (date: Dayjs, message: string | undefined = undefined): RuleObject => ({
  validator: (_, value: Dayjs | undefined) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value.isBefore(date) || value.isSame(date, 'day')) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(
      message ?? `Date must be before ${date.format('MM/DD/YYYY')}`,
    ));
  },
});

export const dateBeforeOrEqual = (date: Dayjs, message: string | undefined = undefined): RuleObject => ({
  validator: (_, value: Dayjs | undefined) => {
    if (!value) {
      return Promise.resolve();
    }
    if (value.isBefore(date) || value.isSame(date, 'day')) {
      return Promise.resolve();
    }
    return Promise.reject(new Error(
      message ?? `Date must be before or equal to ${date.format('MM/DD/YYYY')}`,
    ));
  },
});

export const dateAfterToday = (message = 'Date must be after today'): RuleObject => dateAfter(dayjs(), message);
/**
 * Pure validator: copy-into season year must be >= copy-from season year (same year allowed).
 */
export function copyIntoSeasonGteFromYear(
  copyFromSeason: string | undefined,
  copyIntoSeason: string,
  message = 'Copy into year must be greater than or equal to the "Copy from" year.',
): Promise<void> {
  if (!copyIntoSeason || !copyFromSeason) {
    return Promise.resolve();
  }
  const intoYear = Number(copyIntoSeason);
  const fromYear = Number(copyFromSeason);
  if (!Number.isNaN(intoYear) && !Number.isNaN(fromYear) && intoYear < fromYear) {
    return Promise.reject(new Error(message));
  }
  return Promise.resolve();
}

/**
 * Pure validator: copy-into term and season cannot both match copy-from.
 */
export function copyIntoNotSameAsFrom(
  copyFromTerm: string | undefined,
  copyFromSeason: string | undefined,
  copyIntoTerm: string | undefined,
  copyIntoSeason: string,
  message = 'Copy into term and year cannot match the "Copy from" selection.',
): Promise<void> {
  if (!copyIntoSeason || !copyFromTerm || !copyFromSeason) {
    return Promise.resolve();
  }
  if (copyIntoTerm === copyFromTerm && copyIntoSeason === copyFromSeason) {
    return Promise.reject(new Error(message));
  }
  return Promise.resolve();
}
